import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity, Search, ChevronDown, FileDown, TrendingUp, TrendingDown,
    Calendar as CalendarIcon, AlertCircle, Loader2, Moon, Droplets, Dumbbell, Smile, Flame, Sparkles
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [30, 90, 180];
const SORT_OPTIONS = [
    { key: 'frequency', de: 'Häufigkeit', en: 'Frequency' },
    { key: 'severity', de: 'Schwere', en: 'Severity' },
    { key: 'recency', de: 'Zuletzt', en: 'Recent' },
];

const CATEGORY_LABELS = {
    head: { de: 'Kopf', en: 'Head' },
    stomach: { de: 'Bauch', en: 'Stomach' },
    back: { de: 'Rücken', en: 'Back' },
    skin: { de: 'Haut', en: 'Skin' },
    energy: { de: 'Energie', en: 'Energy' },
    mood: { de: 'Stimmung', en: 'Mood' },
    sleep: { de: 'Schlaf', en: 'Sleep' },
    other: { de: 'Andere', en: 'Other' },
    general: { de: 'Allgemein', en: 'General' },
    recovery: { de: 'Erholung', en: 'Recovery' },
};

const CORRELATION_META = {
    sleep_hours: { icon: Moon, color: '#6366f1', unit: 'h', precision: 1, de: 'Schlaf', en: 'Sleep' },
    water_ml: { icon: Droplets, color: '#3b82f6', unit: 'ml', precision: 0, de: 'Wasser', en: 'Water' },
    calories: { icon: Flame, color: '#f97316', unit: 'kcal', precision: 0, de: 'Kalorien', en: 'Calories' },
    workouts_per_day: { icon: Dumbbell, color: '#22c55e', unit: '', precision: 1, de: 'Workouts/Tag', en: 'Workouts/day' },
    mood_score: { icon: Smile, color: '#ec4899', unit: '/5', precision: 1, de: 'Stimmung', en: 'Mood' },
};

function MiniLineChart({ series, color = '#ef4444', width = 100, height = 28 }) {
    if (!series || series.length === 0) {
        return (
            <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-7">
                <line x1="0" y1={height/2} x2={width} y2={height/2} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 3" />
            </svg>
        );
    }
    const vals = series.map(s => s.severity);
    const min = Math.min(...vals, 0);
    const max = Math.max(...vals, 10);
    const range = Math.max(0.01, max - min);
    const stepX = vals.length > 1 ? width / (vals.length - 1) : width;
    const pts = vals.map((v, i) => [i * stepX, height - ((v - min) / range) * (height - 4) - 2]);
    const path = 'M ' + pts.map(p => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L ');
    return (
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-7">
            <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
            {pts.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="1.5" fill={color} />
            ))}
        </svg>
    );
}

function WeeklyBars({ weeks, maxCount, color = '#ef4444' }) {
    return (
        <div className="flex items-end gap-0.5 h-8" aria-hidden="true">
            {weeks.map((w, i) => {
                const h = maxCount > 0 ? Math.max(2, (w.count / maxCount) * 100) : 2;
                return (
                    <div
                        key={i}
                        className="flex-1 rounded-sm transition-all"
                        style={{
                            height: `${h}%`,
                            background: w.count > 0 ? color : 'rgb(var(--muted) / 0.4)',
                            opacity: w.count > 0 ? Math.max(0.4, Math.min(1, w.count / Math.max(1, maxCount))) : 0.3,
                        }}
                        title={`Woche ${w.week_start}: ${w.count}`}
                    />
                );
            })}
        </div>
    );
}

export default function SymptomAnalytics() {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const locale = language === 'de' ? deLocale : enUS;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('all');
    const [sortBy, setSortBy] = useState('frequency');
    const [expanded, setExpanded] = useState({});
    const [pdfLoading, setPdfLoading] = useState(false);
    const [insights, setInsights] = useState({}); // symptom -> {loading, data, error}

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/health/symptoms/analytics?days=${days}`);
                setData(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [days]);

    const allCategories = useMemo(() => {
        if (!data?.symptoms) return [];
        const cats = new Set();
        data.symptoms.forEach(s => cats.add(s.category));
        return Array.from(cats);
    }, [data]);

    const filteredSymptoms = useMemo(() => {
        if (!data?.symptoms) return [];
        const q = query.trim().toLowerCase();
        let list = data.symptoms.filter(s => {
            if (category !== 'all' && s.category !== category) return false;
            if (q && !s.symptom.toLowerCase().includes(q)) return false;
            return true;
        });
        if (sortBy === 'severity') {
            list = [...list].sort((a, b) => (b.avg_severity || 0) - (a.avg_severity || 0));
        } else if (sortBy === 'recency') {
            list = [...list].sort((a, b) => (b.last_logged || '').localeCompare(a.last_logged || ''));
        }
        return list;
    }, [data, query, category, sortBy]);

    const t = (de, en) => (language === 'de' ? de : en);
    const catLabel = (c) => CATEGORY_LABELS[c]?.[language] || c;

    const handlePdfExport = async () => {
        setPdfLoading(true);
        try {
            const res = await axios.get(`${API}/api/reports/export/pdf?days=${days}&lang=${language}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bestbuddy_gesundheit_${days}d_${new Date().toISOString().slice(0,10)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('📄 PDF heruntergeladen', '📄 PDF downloaded'));
        } catch (e) {
            console.error(e);
            toast.error(t('PDF-Export fehlgeschlagen', 'PDF export failed'));
        } finally {
            setPdfLoading(false);
        }
    };

    const [dossierLoading, setDossierLoading] = useState(false);
    const handleDossierExport = async () => {
        setDossierLoading(true);
        try {
            const res = await axios.get(`${API}/api/reports/nanouk-dossier?days=${days}&language=${language}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `nanouk_dossier_${days}d_${new Date().toISOString().slice(0,10)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('🐺 Nanouk Dossier heruntergeladen', '🐺 Nanouk Dossier downloaded'));
        } catch (e) {
            console.error(e);
            toast.error(t('Dossier-Export fehlgeschlagen', 'Dossier export failed'));
        } finally {
            setDossierLoading(false);
        }
    };

    const [aiExportLoading, setAiExportLoading] = useState(false);
    const handleAiExport = async () => {
        setAiExportLoading(true);
        try {
            const res = await axios.get(`${API}/api/reports/ai-export?days=${days}&language=${language}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/markdown' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bestbuddy_ai_export_${days}d_${new Date().toISOString().slice(0,10)}.md`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('🤝 KI-Export heruntergeladen – lade die Datei z. B. in ChatGPT hoch', '🤝 AI export downloaded – upload the file to ChatGPT etc.'));
        } catch (e) {
            console.error(e);
            toast.error(t('KI-Export fehlgeschlagen', 'AI export failed'));
        } finally {
            setAiExportLoading(false);
        }
    };

    const loadInsight = async (symptomName) => {
        const key = symptomName;
        setInsights(prev => ({ ...prev, [key]: { loading: true } }));
        try {
            const res = await axios.get(
                `${API}/api/health/symptoms/insight?symptom=${encodeURIComponent(symptomName)}&days=${days}&language=${language}`);
            setInsights(prev => ({ ...prev, [key]: { loading: false, data: res.data } }));
        } catch (e) {
            console.error('Insight error:', e);
            setInsights(prev => ({ ...prev, [key]: { loading: false, error: true } }));
            toast.error(t('Nanouk konnte gerade nicht antworten', 'Nanouk could not respond'));
        }
    };

    const handleSuggestedAction = (action) => {
        if (!action) return;
        switch (action) {
            case 'sleep_reminder':
                navigate('/settings?focus=notifications');
                break;
            case 'log_water':
            case 'review_nutrition':
                navigate('/nutrition');
                break;
            case 'log_mood':
                navigate('/dashboard');
                break;
            case 'log_workout':
                navigate('/fitness');
                break;
            default:
                break;
        }
    };

    if (loading) {
        return (
            <div className="p-4 rounded-2xl bg-card border animate-pulse" data-testid="symptom-analytics-loading">
                <div className="h-6 w-48 bg-muted rounded mb-3"></div>
                <div className="h-32 bg-muted/50 rounded"></div>
            </div>
        );
    }
    if (!data) return null;

    const sevColor = (sev) => {
        if (sev == null) return '#6b7280';
        if (sev >= 7) return '#ef4444';
        if (sev >= 4) return '#f59e0b';
        return '#22c55e';
    };

    return (
        <div className="rounded-2xl bg-card border p-4 space-y-3" data-testid="symptom-analytics">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-rose-400" />
                    <h3 className="text-base font-semibold">{t('Symptom-Analyse', 'Symptom Analytics')}</h3>
                    <span className="text-xs text-muted-foreground">
                        ({data.total_count} {t('Einträge', 'logs')} · {data.symptom_free_days} {t('symptomfreie Tage', 'symptom-free days')})
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex gap-1">
                        {RANGES.map(d => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                                    days === d ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`symptom-range-${d}`}
                            >
                                {d}T
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={handlePdfExport}
                        disabled={pdfLoading}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                        data-testid="symptom-pdf-export"
                    >
                        {pdfLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                        PDF
                    </button>
                    <button
                        onClick={handleDossierExport}
                        disabled={dossierLoading}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-violet-500/40 bg-gradient-to-r from-violet-500/15 to-sky-500/15 text-violet-500 hover:from-violet-500/25 hover:to-sky-500/25 transition-colors disabled:opacity-50"
                        data-testid="nanouk-dossier-export"
                        title={t('Premium 4-Seiten Personal-Health-Report', 'Premium 4-page personal health report')}
                    >
                        {dossierLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {t('Nanouk Dossier', 'Nanouk Dossier')}
                    </button>
                    <button
                        onClick={handleAiExport}
                        disabled={aiExportLoading}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                        data-testid="ai-export-btn"
                        title={t('Strukturierter Daten-Export für ChatGPT & andere KI-Assistenten', 'Structured data export for ChatGPT & other AI assistants')}
                    >
                        {aiExportLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                        {t('KI-Export', 'AI Export')}
                    </button>
                </div>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder={t('Symptom suchen…', 'Search symptom…')}
                        className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-muted border border-border outline-none focus:border-primary"
                        data-testid="symptom-search"
                    />
                </div>
                <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-muted border border-border outline-none focus:border-primary"
                    data-testid="symptom-category-filter"
                >
                    <option value="all">{t('Alle Kategorien', 'All categories')}</option>
                    {allCategories.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
                </select>
                <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-muted border border-border outline-none focus:border-primary"
                    data-testid="symptom-sort"
                >
                    {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{language === 'de' ? o.de : o.en}</option>)}
                </select>
            </div>

            {/* List */}
            {filteredSymptoms.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-8" data-testid="symptom-analytics-empty">
                    {data.total_count === 0
                        ? t('Keine Symptome im gewählten Zeitraum. 🎉', 'No symptoms in this period. 🎉')
                        : t('Keine Treffer für diese Filter.', 'No results for these filters.')}
                </div>
            ) : (
                <div className="space-y-2">
                    {filteredSymptoms.map((s, idx) => {
                        const isOpen = expanded[s.symptom] || false;
                        const maxWeekCount = Math.max(...s.weekly_trend.map(w => w.count), 1);
                        const sev = s.avg_severity;
                        const sColor = sevColor(sev);
                        return (
                            <div key={`${s.symptom}-${idx}`} className="rounded-xl border border-border bg-muted/20 overflow-hidden" data-testid={`symptom-card-${s.symptom}`}>
                                {/* Header */}
                                <button
                                    type="button"
                                    onClick={() => setExpanded(prev => ({ ...prev, [s.symptom]: !prev[s.symptom] }))}
                                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-muted/40 transition-colors"
                                    data-testid={`symptom-card-toggle-${s.symptom}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <span className="text-2xl shrink-0" style={{ color: sColor }}>●</span>
                                        <div className="text-left min-w-0">
                                            <p className="text-sm font-semibold truncate">{s.symptom}</p>
                                            <p className="text-[10px] text-muted-foreground">
                                                {catLabel(s.category)} · {s.last_logged && format(parseISO(s.last_logged), 'd. MMM', { locale })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="text-right">
                                            <p className="text-base font-bold tabular-nums">{s.count}×</p>
                                            <p className="text-[10px] text-muted-foreground">
                                                Ø {sev?.toFixed(1) ?? '—'}/10
                                            </p>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>

                                <AnimatePresence>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden border-t border-border bg-card/50"
                                        >
                                            <div className="p-3 space-y-3">
                                                {/* Severity sparkline */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                            {t('Schwere-Verlauf', 'Severity over time')}
                                                        </span>
                                                        <span className="text-[10px] text-muted-foreground">
                                                            Max {s.max_severity}/10
                                                        </span>
                                                    </div>
                                                    <MiniLineChart series={s.severity_series} color={sColor} />
                                                </div>

                                                {/* Weekly bars */}
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                                        {t(`Häufigkeit pro Woche (${s.weekly_trend.length})`, `Frequency per week (${s.weekly_trend.length})`)}
                                                    </p>
                                                    <WeeklyBars weeks={s.weekly_trend} maxCount={maxWeekCount} color={sColor} />
                                                </div>

                                                {/* Correlations */}
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                                        {t('An Symptom-Tagen vs. symptomfreien Tagen', 'On symptom days vs. symptom-free days')}
                                                    </p>
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                        {Object.entries(CORRELATION_META).map(([k, meta]) => {
                                                            const c = s.correlations[k];
                                                            if (!c || (c.on_symptom == null && c.on_clean == null)) return null;
                                                            const Icon = meta.icon;
                                                            const fmt = (v) => v == null ? '—' : v.toFixed(meta.precision);
                                                            const onSym = fmt(c.on_symptom);
                                                            const onCln = fmt(c.on_clean);
                                                            const delta = c.delta;
                                                            const deltaColor = delta == null ? 'text-muted-foreground'
                                                                : delta < 0 ? 'text-rose-500' : delta > 0 ? 'text-emerald-500' : 'text-muted-foreground';
                                                            const DeltaIcon = delta == null ? null : delta < 0 ? TrendingDown : TrendingUp;
                                                            return (
                                                                <div key={k} className="p-2 rounded-lg bg-muted/40 border border-border" data-testid={`symptom-corr-${s.symptom}-${k}`}>
                                                                    <div className="flex items-center gap-1.5 mb-1">
                                                                        <Icon className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                                                                        <span className="text-[10px] font-medium truncate">{language === 'de' ? meta.de : meta.en}</span>
                                                                    </div>
                                                                    <div className="flex items-baseline gap-1 text-[10px]">
                                                                        <span className="font-bold tabular-nums text-foreground">{onSym}{meta.unit}</span>
                                                                        <span className="text-muted-foreground">vs</span>
                                                                        <span className="text-muted-foreground tabular-nums">{onCln}{meta.unit}</span>
                                                                    </div>
                                                                    {DeltaIcon && (
                                                                        <div className={`flex items-center gap-0.5 text-[10px] font-semibold mt-0.5 ${deltaColor}`}>
                                                                            <DeltaIcon className="w-2.5 h-2.5" />
                                                                            {delta > 0 ? '+' : ''}{delta?.toFixed(meta.precision)}{meta.unit}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Nanouk AI Insight */}
                                                {(() => {
                                                    const ins = insights[s.symptom];
                                                    if (!ins) {
                                                        return (
                                                            <button
                                                                type="button"
                                                                onClick={() => loadInsight(s.symptom)}
                                                                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-violet-500/30 bg-violet-500/10 text-violet-500 hover:bg-violet-500/20 transition-colors"
                                                                data-testid={`symptom-insight-trigger-${s.symptom}`}
                                                            >
                                                                <Sparkles className="w-3.5 h-3.5" />
                                                                {t("Nanouk's Einblick anzeigen", "Show Nanouk's insight")}
                                                            </button>
                                                        );
                                                    }
                                                    if (ins.loading) {
                                                        return (
                                                            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-3" data-testid={`symptom-insight-loading-${s.symptom}`}>
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500" />
                                                                {t('Nanouk denkt nach…', 'Nanouk is thinking…')}
                                                            </div>
                                                        );
                                                    }
                                                    if (ins.error || !ins.data?.insight) {
                                                        return (
                                                            <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-2">
                                                                {t('Nicht genug Daten für einen Einblick.', 'Not enough data for an insight.')}
                                                            </div>
                                                        );
                                                    }
                                                    const sa = ins.data.suggested_action;
                                                    return (
                                                        <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3 space-y-2" data-testid={`symptom-insight-${s.symptom}`}>
                                                            <div className="flex items-start gap-2">
                                                                <div className="w-7 h-7 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                                                                    <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-400 mb-1">Nanouk</p>
                                                                    <p className="text-xs leading-relaxed whitespace-pre-wrap text-foreground/90">
                                                                        {ins.data.insight}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {sa && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSuggestedAction(sa.action)}
                                                                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
                                                                    data-testid={`symptom-insight-action-${s.symptom}`}
                                                                >
                                                                    <Sparkles className="w-3.5 h-3.5" />
                                                                    {sa.label}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* Meta info */}
                                                <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground pt-1">
                                                    <div className="inline-flex items-center gap-1">
                                                        <CalendarIcon className="w-3 h-3" />
                                                        {t('Erstmals', 'First')}: {s.first_logged && format(parseISO(s.first_logged), 'd. MMM', { locale })}
                                                    </div>
                                                    <div className="inline-flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" />
                                                        {s.days_with_this} {t('verschiedene Tage', 'distinct days')}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
